import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { createApiCall } from "services";

export interface GoogleFont {
  id: string;
  name: string;
}

interface GoogleFontsResponse {
  data: GoogleFont[];
}

export const useGoogleFonts = (): UseQueryResult<
  GoogleFontsResponse,
  Error
> => {
  return useQuery({
    queryKey: ["google-fonts"],
    queryFn: async () => {
      const response = await createApiCall({
        method: "GET",
        url: "common/google-fonts",
        // Stop the browser making this a CONDITIONAL request. `createApiCall`
        // sets `cache: 'no-cache'`, which is a fetch option and not an HTTP
        // header, so it does nothing here; the browser sent If-None-Match and
        // got 304 with no body. `no-store` is the real header and it asks for
        // the payload every time. Cheap: the server already memoises this list
        // for 24h and react-query holds it for 24h on this side.
        headers: { "Cache-Control": "no-store" },
      });

      // NEVER return undefined. A repeat request is answered 304 Not Modified from
      // the browser cache, which carries no body, so `response.data` is undefined —
      // and react-query rejects an undefined value outright ("Query data cannot be
      // undefined"), so the whole query errors and the font picker falls back to
      // its 6-item list with a warning across the field. Nothing is actually wrong
      // with the font list at that point; it is a cache response being read as a
      // payload. Same failure family as the company-fonts hooks: this axios layer
      // does not guarantee a body, so a hook must not assume one.
      const payload = response?.data as GoogleFontsResponse | undefined;
      // THROW rather than returning an empty list. Returning `{data: []}` would
      // satisfy react-query and then be CACHED for the 24h staleTime, pinning the
      // picker to its 6-font fallback for the rest of the session. Throwing lets
      // the configured retries run and keeps the failure visible.
      if (!Array.isArray(payload?.data)) {
        throw new Error("Google Fonts list came back empty.");
      }
      return payload;
    },
    staleTime: 1000 * 60 * 60 * 24,
    retry: 2,
  }) as UseQueryResult<GoogleFontsResponse, Error>;
};
